import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { CalendarIcon, PlusCircle, Loader2, AlertCircle, Sparkles, Camera } from 'lucide-react';
import imageCompression from 'browser-image-compression';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAppStore } from '@/lib/data';
import { useAuth } from '@/hooks/use-auth';
import { useActiveWorkspace } from '@/hooks/use-active-workspace';
import { AnalyticsService } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';
import { StreakService, ACTIVITY_TYPES } from '@/lib/streak-service';
import { AddCategorySheet } from '@/components/budgets/add-category-sheet';
import { kwachaAI, type CategorySuggestion } from '@/lib/kwacha-ai-client';
import type { Transaction, Category } from '@/lib/types';

import { Checkbox } from '@/components/ui/checkbox';
import { Label as UILabel } from '@/components/ui/label';
import { seedTransactions } from '@/lib/seed-data';

// Moved outside to prevent re-initialization on every render
const formSchema = z.object({
  date: z.date({ required_error: "A date is required." }),
  description: z.string().min(1, 'Description is required'),
  amount: z.coerce.number().positive('Amount must be positive'),
  type: z.enum(['income', 'expense']),
  category_id: z.string().min(1, 'Category is required'),
  workspace: z.enum(['personal', 'business']),
  keepOpen: z.boolean().default(false),
});

type TransactionFormValues = z.infer<typeof formSchema>;

export function AddTransactionSheet() {
  const { toast } = useToast();
  const { categories, addTransaction } = useAppStore();
  const { user } = useAuth();
  const { activeWorkspace } = useActiveWorkspace();
  
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCategorySheet, setShowCategorySheet] = useState(false);
  
  const [aiSuggestion, setAiSuggestion] = useState<CategorySuggestion | null>(null);
  const [isCategorizing, setIsCategorizing] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const form = useForm<TransactionFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date(),
      description: '',
      amount: 0,
      type: 'expense',
      category_id: '',
      workspace: activeWorkspace || 'personal',
      keepOpen: false,
    },
  });

  const transactionType = form.watch('type');
  const description = form.watch('description');
  const categoryId = form.watch('category_id');

  const selectedCategory = useMemo(() => 
    categories.find(c => c.id === categoryId), 
    [categories, categoryId]
  );

  // Get quick suggestions based on selected category
  const quickSuggestions = useMemo(() => {
    if (!selectedCategory) return [];
    
    const catName = selectedCategory.name.toLowerCase();
    return seedTransactions
      .filter(t => {
        const seedCat = t.category.toLowerCase();
        return seedCat === catName || 
               catName.includes(seedCat) || 
               seedCat.includes(catName) ||
               (catName === 'transportation' && seedCat === 'transport') ||
               (catName === 'food & groceries' && (seedCat === 'groceries' || seedCat === 'food & dining'));
      })
      .map(t => t.description)
      .slice(0, 5);
  }, [selectedCategory]);

  const handleSuggestionClick = (desc: string) => {
    form.setValue('description', desc);
  };

  const filteredCategories = useMemo(() => {
    return categories.filter(cat => cat.type === transactionType);
  }, [categories, transactionType]);

  useEffect(() => {
    if (open && filteredCategories.length === 0) {
      toast({
        title: 'No Categories Found',
        description: `You don't have any ${transactionType} categories. Create categories first.`,
        variant: 'destructive',
      });
    }
  }, [open, filteredCategories.length, transactionType, toast]);

  useEffect(() => {
    if (activeWorkspace) {
      form.setValue('workspace', activeWorkspace as 'personal' | 'business');
    }
  }, [activeWorkspace, form]);

  useEffect(() => {
    const currentCatId = form.getValues('category_id');
    if (currentCatId && !filteredCategories.find(c => c.id === currentCatId)) {
      form.setValue('category_id', '');
    }
  }, [transactionType, filteredCategories, form]);

  useEffect(() => {
    if (!description || description.trim().length < 3) {
      setAiSuggestion(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsCategorizing(true);
      try {
        const userId = user?.id || 'anonymous';
        const res = await kwachaAI.categorize(description, userId);
        const localCategory = categories.find(
          c => c.name.toLowerCase() === res.suggestion.category.toLowerCase() && c.type === transactionType
        );
        if (localCategory && res.suggestion.confidence > 0.4) {
           setAiSuggestion({
             category: localCategory.name,
             confidence: res.suggestion.confidence,
             category_id: localCategory.id
           });
        } else {
           setAiSuggestion(null);
        }
      } catch (e) {
        console.error("Kwacha AI categorizer failed:", e);
      } finally {
        setIsCategorizing(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [description, user?.id, categories, transactionType]);

  const acceptAiSuggestion = () => {
    if (aiSuggestion?.category_id) {
      form.setValue('category_id', aiSuggestion.category_id);
      setAiSuggestion(null);
    }
  };

  const handleReceiptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    setIsScanning(true);
    try {
      const options = { maxSizeMB: 0.8, maxWidthOrHeight: 1920, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('receipts').upload(fileName, compressedFile);

      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(fileName);
      const ocrResult = await kwachaAI.scanReceipt(publicUrl, user.id);

      if (ocrResult.merchant) form.setValue('description', ocrResult.merchant);
      if (ocrResult.total) form.setValue('amount', ocrResult.total);
      if (ocrResult.date) form.setValue('date', new Date(ocrResult.date));
      
      if (ocrResult.suggested_category) {
        const cat = categories.find(c => 
          c.name.toLowerCase() === ocrResult.suggested_category?.toLowerCase() && 
          c.type === transactionType
        );
        if (cat) form.setValue('category_id', cat.id);
      }

      toast({ title: 'Receipt Scanned', description: `Extracted details from ${ocrResult.merchant || 'receipt'}.` });
    } catch (error) {
      console.error('OCR Error:', error);
      toast({ title: 'Scan Failed', description: 'Failed to process receipt.', variant: 'destructive' });
    } finally {
      setIsScanning(false);
      event.target.value = '';
    }
  };

  async function onSubmit(values: TransactionFormValues) {
    setIsSubmitting(true);
    try {
      const selectedCategory = categories.find(c => c.id === values.category_id);
      const newTransaction: Transaction = {
        id: crypto.randomUUID(),
        ...values,
        date: format(values.date, 'yyyy-MM-dd'),
        category: selectedCategory?.name || 'General',
      };

      await addTransaction(newTransaction);
      if (user?.id) {
        const streakResult = await StreakService.recordActivity(user.id, ACTIVITY_TYPES.TRANSACTION_ADDED);
        if (streakResult?.current_streak === 1) {
          toast({ title: '🔥 Streak started!', description: 'Log a transaction every day to keep it going.' });
        } else if (streakResult?.is_new_milestone) {
          toast({ title: `🏆 ${streakResult.current_streak} day streak!`, description: 'Amazing consistency — keep it up!' });
        }
        window.dispatchEvent(new Event('streak-updated'));
        AnalyticsService.trackEvent({
          event_type: 'transaction',
          user_id: user.id,
          properties: { ...values, category_name: newTransaction.category },
        });
      }

      toast({ title: 'Transaction Added', description: `${values.description} recorded.` });
      
      if (values.keepOpen) {
        form.reset({ ...form.control._defaultValues, date: values.date, workspace: values.workspace, keepOpen: true });
      } else {
        form.reset({ ...form.control._defaultValues, date: new Date(), workspace: values.workspace });
        setOpen(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save transaction.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" className="gap-1 shadow-sm">
          <PlusCircle className="h-4 w-4" />
          Add Transaction
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <div className="flex justify-between items-center">
            <SheetTitle>Add Transaction</SheetTitle>
            <div className="relative">
              <input type="file" accept="image/*" onChange={handleReceiptUpload} className="hidden" id="receipt-upload" disabled={isScanning} />
              <label htmlFor="receipt-upload" className="cursor-pointer">
                <Button type="button" variant="outline" size="sm" className="gap-1.5 h-8 border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-300" asChild disabled={isScanning}>
                  <span>
                    {isScanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                    {isScanning ? 'Scanning...' : 'Scan Receipt'}
                  </span>
                </Button>
              </label>
            </div>
          </div>
          <SheetDescription>Recorded transactions update your budget in real-time.</SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 mt-6 flex-1">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Rent, Coffee, Freelance..." {...field} />
                  </FormControl>
                  {quickSuggestions.length > 0 && !field.value && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {quickSuggestions.map((suggestion) => (
                        <Badge
                          key={suggestion}
                          variant="secondary"
                          className="cursor-pointer hover:bg-slate-200 transition-colors py-1 px-2 text-[11px] font-normal"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                        <Input type="number" step="0.01" className="pl-7" placeholder="0.00" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>Category</FormLabel>
                    {aiSuggestion && !field.value && (
                      <button type="button" onClick={acceptAiSuggestion} className="flex items-center gap-1.5 text-[11px] font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200 hover:bg-indigo-100 transition-colors">
                        <Sparkles className="h-3 w-3 text-indigo-500" />
                        Kwacha AI suggests {aiSuggestion.category}
                      </button>
                    )}
                    {isCategorizing && !aiSuggestion && !field.value && description.length >= 3 && (
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Analyzing...</div>
                    )}
                  </div>
                  <Select onValueChange={(val) => { field.onChange(val); setAiSuggestion(null); }} value={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {filteredCategories.length > 0 ? filteredCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      )) : (
                        <div className="p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-2">No categories found</p>
                          <Button type="button" variant="outline" size="sm" onClick={() => setShowCategorySheet(true)} className="text-xs">Create Category</Button>
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button variant="outline" className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                            {field.value ? format(field.value, "MMM d, yyyy") : <span>Pick date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="workspace"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Workspace</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="keepOpen"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <div className="space-y-1 leading-none">
                    <UILabel className="text-sm font-medium">Keep open to add another</UILabel>
                    <p className="text-xs text-muted-foreground">Quickly enter multiple transactions.</p>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-6">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Transaction
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
    <AddCategorySheet open={showCategorySheet} onOpenChange={setShowCategorySheet} />
    </>
  );
}
